/* import __COLOCATED_TEMPLATE__ from './edit-attribute-operation.hbs'; */
/* RESPONSIBLE TEAM: team-actions */
import Component from '@glimmer/component';
import type IntlService from 'embercom/services/intl';
import { inject as service } from '@ember/service';
import { type OutputParam } from 'embercom/components/workflows/graph-editor/node-items/steps/fin-custom';
import { type AttributeInfoResolver } from '@intercom/embercom-prosemirror-composer';
import type Composer from '@intercom/embercom-prosemirror-composer/lib/composer';
import type AttributeDescriptor from 'embercom/models/operator/visual-builder/attribute-descriptor';
import type { NodeSelection } from 'prosemirror-state';

interface LocalAttributeInfoResolver extends AttributeInfoResolver {
  localAttributes: {
    heading: string;
    attributes: $TSFixMe[];
  }[];
}

interface Args {
  resolver: LocalAttributeInfoResolver;
  openModal: (selectedTemporaryAttribute?: OutputParam) => void;
  composer: Composer;
}

export default class EditAttributeOperation extends Component<Args> {
  @service declare intl: IntlService;
  @service declare appService: any;

  get selectedTemporaryAttribute(): OutputParam | undefined {
    let matchingAttribute = this._findMatchingTemporaryAttribute();

    if (matchingAttribute) {
      let id = matchingAttribute.get('identifier').split('.')[
        matchingAttribute.get('identifier').split('.').length - 1
      ];
      let outputParam: OutputParam = {
        id,
        name: matchingAttribute.get('name'),
        description: matchingAttribute.get('description'),
        descriptorType: matchingAttribute.type,
        listOptions: [],
      };
      if (matchingAttribute.get('type') === 'workflow_attribute_descriptor_list') {
        let listOptions = matchingAttribute.get('listOptions');

        listOptions.forEach((option: any) => {
          outputParam.listOptions.push({
            id: option.get('id'),
            value: option.get('label'),
          });
        });
      }
      return outputParam;
    }

    return undefined;
  }

  get selectedAttributeObjectType() {
    if (this.selectedTemporaryAttribute) {
      return this.intl.t('workflows.graph-editor.temporary-attribute-modal.title');
    }

    let subjectIdentifier = (this.args.composer.state.prosemirrorState.selection as NodeSelection)
      .node.attrs.subjectIdentifier;
    let matchingAttribute = this.appService.app?.allowedAttributes.find((attr: $TSFixMe) => {
      return attr.templatableIdentifier === subjectIdentifier;
    });

    if (matchingAttribute) {
      return `${matchingAttribute.objectTypeName} attribute`;
    }

    return this.intl.t('workflows.graph-editor.attribute');
  }

  get selectedAttributeDescription() {
    if (this.selectedTemporaryAttribute) {
      return this.selectedTemporaryAttribute.description;
    }

    let subjectIdentifier = (this.args.composer.state.prosemirrorState.selection as NodeSelection)
      .node.attrs.subjectIdentifier;
    let matchingAttribute = this.appService.app?.allowedAttributes.find((attr: $TSFixMe) => {
      return attr.templatableIdentifier === subjectIdentifier;
    });

    if (matchingAttribute) {
      return matchingAttribute.description;
    }

    return '';
  }

  get isTemporaryAttributeSelected() {
    return this._findMatchingTemporaryAttribute() !== undefined;
  }

  get selectedAttributeFormat() {
    let descriptorType: string | undefined;
    if (this.isTemporaryAttributeSelected) {
      descriptorType = this.selectedTemporaryAttribute?.descriptorType;
    } else {
      let subjectIdentifier = (this.args.composer.state.prosemirrorState.selection as NodeSelection)
        .node.attrs.subjectIdentifier;
      let matchingAttribute = this.appService.app?.allowedAttributes.find((attr: $TSFixMe) => {
        return attr.templatableIdentifier === subjectIdentifier;
      });

      descriptorType = matchingAttribute.type;
    }

    return descriptorType;
  }

  get selectedAttributeFormatString() {
    let descriptorType = this.selectedAttributeFormat;
    switch (descriptorType) {
      case 'string':
        return this.intl.t(
          'operator.workflows.visual-builder.fin-custom-settings.results.attribute-descriptor-formats.string',
        );
      case 'integer':
        return this.intl.t(
          'operator.workflows.visual-builder.fin-custom-settings.results.attribute-descriptor-formats.integer',
        );
      case 'float':
        return this.intl.t(
          'operator.workflows.visual-builder.fin-custom-settings.results.attribute-descriptor-formats.decimal',
        );
      case 'boolean':
        return this.intl.t(
          'operator.workflows.visual-builder.fin-custom-settings.results.attribute-descriptor-formats.boolean',
        );
      case 'date':
        return this.intl.t(
          'operator.workflows.visual-builder.fin-custom-settings.results.attribute-descriptor-formats.date-time',
        );
      case 'list':
      case 'workflow_attribute_descriptor_list':
        return this.intl.t(
          'operator.workflows.visual-builder.fin-custom-settings.results.attribute-descriptor-formats.list',
        );
      default:
        return undefined;
    }
  }

  get hiddenListOptionsCount() {
    if (!this.selectedTemporaryAttribute) {
      return 0;
    }

    let listOptions: HTMLElement = document.querySelector('.options-list') as HTMLElement;
    let optionItems = listOptions.querySelectorAll('.option-item');

    if (optionItems === undefined || optionItems.length === 0) {
      return 0;
    }

    let hiddenCount = 0;

    this.selectedTemporaryAttribute.listOptions.forEach((_, index) => {
      let optionItem = optionItems[index] as HTMLElement;
      if (optionItem) {
        if (optionItem.offsetLeft + optionItem.clientWidth > listOptions.offsetWidth) {
          hiddenCount++;
          optionItem.classList.add('hidden');
        } else {
          optionItem.classList.remove('invisible');
        }
      }
    });

    return hiddenCount;
  }

  _findMatchingTemporaryAttribute(): AttributeDescriptor | undefined {
    if (
      this.args.resolver.localAttributes.length === 0 ||
      this.args.resolver.localAttributes[0].heading !== 'Temporary attributes'
    ) {
      return undefined;
    }

    let tempAttributes = this.args.resolver.localAttributes[0].attributes as AttributeDescriptor[];

    let matchingAttribute = tempAttributes.find((attr) => {
      let identifier = attr.get('identifier');
      if (
        (
          this.args.composer.state.prosemirrorState.selection as NodeSelection
        ).node.attrs.subjectIdentifier.includes(identifier)
      ) {
        return attr;
      }

      return undefined;
    });

    return matchingAttribute;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::FinCustom::AiPromptDataPopover::EditAttributeOperation': typeof EditAttributeOperation;
  }
}
